import type { PermissionAction, SystemModule, User } from '@/types';
import { isAdmin, isCoach, isCoordinator } from '@/utils/roleChecks';

/**
 * THE capability helper. Every new gate should call `userHasPermission` (or one of
 * the named capability helpers below) instead of comparing roles inline.
 *
 * WHY THIS EXISTS: the Roles & Permissions matrix is written to Firestore and
 * denormalized onto `user.permissions`, but only ~20 call sites ever read it —
 * against ~173 that hardcode role lists. That drift is what made the matrix lie:
 * Coordinator was granted attendance_view + attendance_manage in the tab, while
 * `/attendance` resolved its surface with `isAdmin(u) || isCoach(u)` and silently
 * sent coordinators to the student view.
 *
 * Resolution order (first match wins):
 *   1. admin / super_admin      -> everything
 *   2. coordinator baseline     -> the branch-operations modules below
 *   3. the denormalized matrix  -> user.permissions[module][action]
 *
 * Pure and user-argument-based (not a hook) so route resolvers, nav config and
 * non-React code can all share one answer. AuthProvider.hasPermission delegates
 * here with the impersonated user, so impersonation is honoured automatically.
 */

/**
 * Modules a coordinator can always use, regardless of matrix state. A coordinator
 * is branch-operations staff: they run the day-to-day floor (members, classes,
 * attendance, subscriptions) but do not get finance mutation or system config.
 *
 * This mirrors the list previously inlined in AuthProvider.hasPermission — kept as
 * a baseline so a half-configured matrix cannot lock branch staff out of their job.
 */
const COORDINATOR_BASELINE_MODULES: ReadonlySet<SystemModule> = new Set<SystemModule>([
    'dashboard_view',
    'users_view',
    'users_manage_students',
    'users_manage_coaches',
    'classes_view',
    'classes_manage',
    'schedule_view',
    'schedule_manage',
    'attendance_view',
    'attendance_checkin',
    'attendance_manage',
    'subscriptions_view',
    'subscriptions_manage',
    'finance_view',
    'finance_income_view',
]);

/** Actions the coordinator baseline covers. Destructive/admin-only actions still fall through to the matrix. */
const COORDINATOR_BASELINE_ACTIONS: ReadonlySet<PermissionAction> = new Set<PermissionAction>([
    'create', 'read', 'update', 'approve', 'reject', 'assign', 'export',
]);

/** Does `user` hold `action` on `module`? The one function every gate should use. */
export function userHasPermission(
    user: User | null | undefined,
    module: SystemModule,
    action: PermissionAction,
): boolean {
    if (!user) return false;

    // 1. Admins bypass everything (matches firestore.rules and RoleGuard).
    if (isAdmin(user)) return true;

    // 2. Coordinator baseline — branch operations.
    if (isCoordinator(user)
        && COORDINATOR_BASELINE_MODULES.has(module)
        && COORDINATOR_BASELINE_ACTIONS.has(action)) {
        return true;
    }

    // 3. The Roles & Permissions matrix, denormalized onto the user doc.
    return user.permissions?.[module]?.[action] === true;
}

// ─── named capability helpers (prefer these at call sites) ──────────────────

/**
 * Can this user mark other members present — i.e. should they get the coach
 * "Mark Attendance" tool rather than the student "My Attendance" history?
 *
 * Coaches are included by role because a coach's grant comes from their role
 * rather than the matrix; coordinators and admins resolve through the matrix path
 * above. Any ONE of the user's roles granting this is enough (multi-role union).
 */
export function canMarkAttendance(user: User | null | undefined): boolean {
    if (!user) return false;
    return isAdmin(user)
        || isCoach(user)
        || isCoordinator(user)
        || userHasPermission(user, 'attendance_checkin', 'create')
        || userHasPermission(user, 'attendance_manage', 'update');
}

/**
 * Can this user see and action belt promotion requests?
 *
 * Admins only — belt rank is the head coach's call (Ahmed, 2026-09-02). Coaches and
 * coordinators may FILE a request on a member's behalf but never approve one, which
 * is exactly what the /beltRequests rule enforces: create is open to member, parent,
 * coach and admin; read-all and update are admin-only.
 *
 * Replaces a literal `currentUser?.role === 'super_admin'` in useBeltRequests, which
 * missed anyone whose admin role arrives via roleIds/roleNames rather than the legacy
 * `role` field — the same multi-role bug hasRoleNamed() exists to solve.
 */
export function canManageBeltPromotions(user: User | null | undefined): boolean {
    return isAdmin(user);
}

/** Staff-shaped surfaces (management nav, coach tooling) rather than member-facing ones. */
export function isStaffCapable(user: User | null | undefined): boolean {
    if (!user) return false;
    return isAdmin(user) || isCoach(user) || isCoordinator(user);
}

// ─── honesty for the Roles & Permissions tab ────────────────────────────────

/**
 * Modules the matrix lets an admin toggle but which NO code enforces yet. The
 * Roles & Permissions tab badges these "not yet enforced" so the tab stops
 * promising capability the app does not deliver.
 *
 * Derived mechanically by extracting every module named in a real gate
 * (hasPermission / userHasPermission / RoleGuard requiredPermission / the
 * coordinator baseline above) and subtracting that from the SystemModule union:
 * 20 of 61 modules are enforced today, 41 are not.
 *
 * Remove a module from this list in the SAME PR that adds a real gate for it.
 */
export const UNENFORCED_MODULES: ReadonlySet<SystemModule> = new Set<SystemModule>([
    'analytics_export',
    'analytics_view',
    'audit_manage',
    'audit_view',
    'branches_manage',
    'branches_view',
    'challenges_manage',
    'challenges_participate',
    'challenges_view',
    'feed_moderate',
    'feed_post',
    'feed_view',
    'files_manage',
    'files_view',
    'finance_expenses_manage',
    'finance_expenses_view',
    'finance_payouts_manage',
    'finance_payouts_view',
    'finance_settings',
    'gamification_belts',
    'gamification_manage',
    'gamification_view',
    'kb_manage',
    'kb_view',
    'leaderboard_view',
    'lms_manage',
    'messages_send',
    'messages_view',
    'notifications_manage',
    'notifications_view',
    'roles_manage',
    'roles_view',
    'settings_profile',
    'subscriptions_plans',
    'support_inbox',
    'support_reply',
    'support_view',
    'users_manage_parents',
    'users_manage_staff',
    'wallet_manage',
    'wallet_view',
]);

export function isModuleEnforced(module: SystemModule): boolean {
    return !UNENFORCED_MODULES.has(module);
}
